import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import type { AdminRole } from '@/lib/api-admin';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
  toast,
} from '@/components/ui';
import { PERMISSION_MENU_GROUPS, toMenuPickBlocks, type MenuPickEntry } from '@/components/Sidebar';
import { RoleMenusNotReadyError, useRoleMenusQuery, useSaveRoleMenusMutation } from '../hooks/useRoleMenus';
import { visibleAdminRoleCodes } from '../types/admin';

/**
 * 메뉴를 정할 수 있는 역할 — 계정에 줄 수 있는 역할(types/admin isDisplayableRole, 계정 생성과 같은 목록).
 * QA-514 후속(운영 결정 B): 최고 관리자·운영 관리자·고객 상담 3개. 숨김 목록을 바꾸면 두 화면이 같이 바뀐다.
 */
export const ROLE_MENU_EDITABLE_ROLES: ReadonlyArray<AdminRole> = visibleAdminRoleCodes;

/**
 * 최고 관리자 탭에서 끌 수 없는 메뉴 — [관리자 계정](이 [역할 권한 관리] 버튼이 있는 곳)을 숨기면 다시 설정하러 들어올 수 없다.
 */
export const SUPER_ADMIN_LOCKED_MENUS: ReadonlyArray<string> = ['/admin/accounts'];

/**
 * QA-514 [역할 권한 관리] — 역할마다 BO 메뉴 전체를 체크박스로 보여 주고 고른 메뉴만 그 역할에 노출한다(최고관리자 전용).
 * "제한 없음"이면 코드 권한대로 전부 보인다. 그 역할의 서버 권한이 없는 메뉴는 체크해도 열리지 않아 막아 둔다.
 */
export const RoleMenusDialog = ({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) => {
  const { t } = useTranslation('common');
  const query = useRoleMenusQuery(open);
  const save = useSaveRoleMenusMutation();
  const [role, setRole] = useState<AdminRole>(ROLE_MENU_EDITABLE_ROLES[0] ?? 'OPS_ADMIN');
  const lockedFor = (r: AdminRole): ReadonlyArray<string> => (r === 'SUPER_ADMIN' ? SUPER_ADMIN_LOCKED_MENUS : []);
  const locked = lockedFor(role);
  // 역할별 편집 중 값. undefined = 아직 안 건드림(서버 값 사용), null = 제한 없음.
  const [drafts, setDrafts] = useState<Partial<Record<AdminRole, string[] | null>>>({});

  useEffect(() => {
    if (!open) setDrafts({});
  }, [open]);

  const saved = useMemo(() => {
    const map = new Map<AdminRole, string[] | null>();
    for (const r of query.data ?? []) map.set(r.role, r.menus);
    return map;
  }, [query.data]);

  const current: string[] | null = drafts[role] !== undefined ? (drafts[role] ?? null) : (saved.get(role) ?? null);
  const unrestricted = current === null;
  const chosen = new Set(current ?? []);
  const dirty = drafts[role] !== undefined && JSON.stringify(drafts[role]) !== JSON.stringify(saved.get(role) ?? null);

  const allowedForRole = (required: ReadonlyArray<AdminRole>) => required.includes(role);
  // 고른 메뉴로 제한할 때는 끌 수 없는 메뉴를 늘 넣어 둔다.
  const setCurrent = (next: string[] | null) =>
    setDrafts((d) => ({ ...d, [role]: next === null ? null : [...new Set([...next, ...locked])] }));
  const toggle = (to: string, on: boolean) => {
    if (locked.includes(to)) return;
    const set = new Set(chosen);
    if (on) set.add(to);
    else set.delete(to);
    setCurrent([...set]);
  };
  const renderItem = (e: MenuPickEntry) => {
    const usableHere = allowedForRole(e.required);
    const isLocked = locked.includes(e.to);
    return (
      <label
        key={e.to}
        className="inline-flex items-center gap-2 text-[13px]"
        style={{ color: usableHere ? 'var(--content-default)' : 'var(--content-soft)' }}
        title={usableHere ? e.to : '이 역할은 서버 권한이 없어 열 수 없습니다'}
      >
        <Checkbox
          checked={usableHere && (current === null || isLocked || chosen.has(e.to))}
          disabled={current === null || notReady || !usableHere || isLocked}
          onCheckedChange={(v) => toggle(e.to, v === true)}
          data-testid={`role-menus-item-${e.to}`}
        />
        {t(e.labelKey)}
        {usableHere ? null : <span className="text-[11px]">(권한 없음)</span>}
        {isLocked ? <span className="text-[11px]" style={{ color: 'var(--content-muted)' }}>(항상 표시)</span> : null}
      </label>
    );
  };
  // 제한을 켤 때는 지금 이 역할이 볼 수 있는 메뉴 전부에서 시작해 필요 없는 것만 끈다.
  const allForRole = PERMISSION_MENU_GROUPS.flatMap((g) => g.entries).filter((e) => allowedForRole(e.required)).map((e) => e.to);

  const onSave = () => {
    save.mutate(
      { role, menus: current },
      {
        onSuccess: () => {
          toast.success('저장되었습니다. 해당 역할 관리자가 화면을 새로고침하면 반영됩니다.');
          setDrafts((d) => ({ ...d, [role]: undefined }));
        },
        onError: (err) =>
          toast.error(
            err instanceof RoleMenusNotReadyError
              ? '역할별 메뉴 저장은 서버 반영 후 사용할 수 있습니다.'
              : friendlyMessage(err, '저장하지 못했습니다.'),
          ),
      },
    );
  };

  const notReady = query.error instanceof RoleMenusNotReadyError;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="role-menus-dialog">
        <DialogHeader>
          <DialogTitle>역할 권한 관리</DialogTitle>
          <DialogDescription>
            역할마다 백오피스에서 보일 메뉴를 고릅니다. 고른 메뉴만 사이드바·즐겨찾는 메뉴·검색에 보입니다. 최고 관리자 탭의 [관리자
            계정]은 이 설정으로 다시 들어올 수 있게 항상 표시됩니다.
          </DialogDescription>
        </DialogHeader>

        {notReady ? (
          <p role="alert" className="rounded-md p-3 text-[13px]" style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }}>
            역할별 메뉴 저장은 서버 반영 후 사용할 수 있습니다. 지금은 역할 권한대로 메뉴가 보입니다.
          </p>
        ) : null}

        <Tabs value={role} onValueChange={(v) => setRole(v as AdminRole)}>
          <TabsList className="flex-wrap">
            {ROLE_MENU_EDITABLE_ROLES.map((r) => (
              <TabsTrigger key={r} value={r} data-testid={`role-menus-tab-${r}`}>
                {t(`admin.roles.codes.${r}`, r)}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {query.isPending ? (
          <Skeleton className="h-[320px] w-full" />
        ) : (
          <div className="flex flex-col gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 text-[13px] font-medium" style={{ color: 'var(--content-strong)' }}>
              <Checkbox
                checked={unrestricted}
                disabled={notReady}
                onCheckedChange={(v) => setCurrent(v === true ? null : allForRole)}
                data-testid="role-menus-unrestricted"
              />
              제한 없음 (역할 권한대로 모든 메뉴 보이기)
            </label>
            <div className="max-h-[50vh] overflow-y-auto rounded-md p-3" style={{ border: '1px solid var(--divider-strong)', opacity: unrestricted ? 0.5 : 1 }}>
              {PERMISSION_MENU_GROUPS.map((g) => {
                const usable = g.entries.filter((e) => allowedForRole(e.required));
                const allOn = usable.length > 0 && usable.every((e) => chosen.has(e.to));
                return (
                  <fieldset key={g.headingKey} className="mb-3">
                    <legend className="mb-1 flex items-center gap-2 text-[12px] font-semibold" style={{ color: 'var(--content-muted)' }}>
                      <Checkbox
                        checked={allOn}
                        disabled={unrestricted || notReady || usable.length === 0}
                        onCheckedChange={(v) => {
                          const set = new Set(chosen);
                          for (const e of usable) {
                            if (v === true) set.add(e.to);
                            else set.delete(e.to);
                          }
                          setCurrent([...set]);
                        }}
                        aria-label={`${String(t(g.headingKey))} 전체`}
                      />
                      {t(g.headingKey)}
                    </legend>
                    {/* QA-509 — 그룹 → 하위 묶음(예: 주문 관리 > 송장 일괄등록) → 메뉴 단계로 보여 준다. */}
                    <div className="grid grid-cols-1 gap-x-4 gap-y-1.5 pl-6 sm:grid-cols-2 lg:grid-cols-3">
                      {toMenuPickBlocks(g.entries).map((block) =>
                        block.kind === 'entry' ? (
                          renderItem(block.entry)
                        ) : (
                          <div key={block.labelKey} className="col-span-full flex flex-col gap-1" data-testid={`role-menus-subgroup-${block.labelKey}`}>
                            <span className="flex items-center gap-1.5 text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
                              <block.icon size={13} aria-hidden="true" />
                              {t(block.labelKey)}
                            </span>
                            <div
                              className="ml-1.5 grid grid-cols-1 gap-x-4 gap-y-1.5 pl-4 sm:grid-cols-2 lg:grid-cols-3"
                              style={{ borderLeft: '1px solid var(--divider-strong)' }}
                            >
                              {block.entries.map((e) => renderItem(e))}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </fieldset>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            닫기
          </Button>
          <Button variant="cta" size="sm" disabled={!dirty || save.isPending || notReady} onClick={onSave} data-testid="role-menus-save">
            {t(`admin.roles.codes.${role}`, role)} 저장
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
